import { ActivityShareType } from '../shared/activity.sharing';
import { DayShortName } from '../shared/schedule.entity';
import { Entity } from '../entity';
import { PhotoSettings } from '../photo.settings/photo.settings';
import { TimesheetEntryStatus } from '../time.billing/timesheet.entry.status';
import { WorkDefaultTime } from '../tenant/schedule';
import { TaxRate } from '../taxrate/tax.rate';
import { InvoiceDateOption } from '../invoicing/invoicing';
import { InvoiceStatus } from '../invoicing/invoicing.enum';
import { CodeFinance } from '../shared/code';
import { SorEvidenceSettings } from '../shared/sor.evidence';
import { TaskGate } from '../tasks/task.evidence';
import { UoM } from '../shared/uom';
import { PlannedWorkSettings, normalizePlannedWorkSettings } from './planned.work.settings';
import { JobMode } from '../enums';

export enum SchedulingLocationType {
    Company = 'Company',
    Technician = 'Technician Scheduling Address'
}

export enum SchedulingRunsheetMode {
    Allocations = 'Allocation', // I will configure the api to return this for null options
    Legacy = 'Legacy',
    Hybrid = 'Hybrid'
}

export const canViewLegacyRunsheet = (mode: SchedulingRunsheetMode) => !mode || [SchedulingRunsheetMode.Hybrid, SchedulingRunsheetMode.Legacy].includes(mode);
export const canViewSchedulerRunsheet = (mode: SchedulingRunsheetMode) => [SchedulingRunsheetMode.Hybrid, SchedulingRunsheetMode.Allocations].includes(mode);


export enum OperationTaskMode {
    Compliance = 'Compliance', // I will configure the api to return this for null options
    Tasks = 'Tasks',
    TasksPro = 'TasksPro',
    Hybrid = 'Hybrid'
}

export enum OperationItemsMode {
    Items = 'Items',
    ItemsPro = 'ItemsPro',
    Hybrid = 'Hybrid'
}

export enum DocumentThemeMode {
    DocumentTheme = 'Document Themes',
    Legacy = 'Legacy Templates'
}

export enum JobReportMode {
    Legacy = 'Document Library',
    DocumentTheme = 'Document Themes',
    Hybrid = 'Hybrid'
}

// Tasks (legacy) = `Tasks`, Tasks (new) = `TasksPro`; Hybrid shows both boards. Stored values never change: released Field App builds parse them.
export const canViewLegacyTaskBoard = (taskMode: OperationTaskMode) => [OperationTaskMode.Hybrid, OperationTaskMode.Tasks].includes(taskMode);
export const canViewNewTaskBoard = (taskMode: OperationTaskMode) => [OperationTaskMode.Hybrid, OperationTaskMode.TasksPro].includes(taskMode);
export const canViewTasks = (taskMode: OperationTaskMode) => canViewLegacyTaskBoard(taskMode) || canViewNewTaskBoard(taskMode);
export const OPERATION_TASK_MODE_LABELS: Partial<Record<OperationTaskMode, string>> = { [OperationTaskMode.Tasks]: 'Tasks (legacy)', [OperationTaskMode.TasksPro]: 'Tasks (new)' };

// Items (legacy) = `Items`, Items (new) = `ItemsPro`; Hybrid shows both boards. Never saved = `Items`: the job editors get the raw stored settings, so the default lives here as well as in the tenant settings read.
export const canViewLegacyItemsBoard = (itemsMode: OperationItemsMode) => !itemsMode || [OperationItemsMode.Hybrid, OperationItemsMode.Items].includes(itemsMode);
export const canViewNewItemsBoard = (itemsMode: OperationItemsMode) => [OperationItemsMode.Hybrid, OperationItemsMode.ItemsPro].includes(itemsMode);
export const OPERATION_ITEMS_MODE_LABELS: Partial<Record<OperationItemsMode, string>> = { [OperationItemsMode.Items]: 'Items (legacy)', [OperationItemsMode.ItemsPro]: 'Items (new)' };

export interface WorkOrderBoardTabLabels {
    readonly tasksLegacy: string;
    readonly tasksNew: string;
    readonly itemsLegacy: string;
    readonly itemsNew: string;
}

// A single board is named plainly; only Hybrid, where both boards sit side by side, tells them apart as old and new.
export const workOrderBoardTabLabels = (taskMode: OperationTaskMode, itemsMode: OperationItemsMode): WorkOrderBoardTabLabels => {

    const tasksHybrid = canViewLegacyTaskBoard(taskMode) && canViewNewTaskBoard(taskMode);
    const itemsHybrid = canViewLegacyItemsBoard(itemsMode) && canViewNewItemsBoard(itemsMode);

    return {
        tasksLegacy: tasksHybrid ? 'Tasks (old)' : 'Tasks',
        tasksNew: tasksHybrid ? 'Tasks (new)' : 'Tasks',
        itemsLegacy: itemsHybrid ? 'Items (old)' : 'Items',
        itemsNew: itemsHybrid ? 'Items (new)' : 'Items',
    };
};

// Which assignee kinds the job/stage Resources card offers; the tenant value is the default
// position of the card's Assignee dropdown, not a hard gate — stored assignments always render.
export enum StageResourceMode {
    SubcontractorOnly = 'Subcontractor only',
    EmployeeOnly = 'Employee only',
    Both = 'Both Subcontractor & Employee' // I will configure the api to return this for null options
}

// How far the Resources card lets a tenant go with an assigned contractor's own field staff. Unlike
// StageResourceMode this one does suppress stored rows: Hide takes the whole sub-list off the card
// for tenants that never work with named field staff (the rows stay readable in the contractor portal).
export enum SubcontractorFieldStaffMode {
    Hide = 'Hide',
    ShowList = 'Show Staff list',
    AllowAdding = 'Allow Adding Staff' // I will configure the api to return this for null options
}

export enum SalesMode {
    Quotes = 'Quotes', // I will configure the api to return this for null options
    Opportunity = 'Opportunity',
    Hybrid = 'Hybrid'
}

// export const canViewQuotes = (salesMode: SalesMode) => [SalesMode.Hybrid, SalesMode.Quotes].includes(salesMode);
// export const canViewOpportunities = (salesMode: SalesMode) => !salesMode || [SalesMode.Hybrid, SalesMode.Opportunity].includes(salesMode);

export const LabourCodeId = 'Labour-00000000-0000-0000-0000-000000000000';
export const LabourCodeName = 'Labour';

export const PrepaymentCodeId = 'Prepayment-00000000-0000-0000-0000-000000000000';
export const PrepaymentCodeName = 'Prepayment';

export interface LabourCode {
    sale: string;
    purchase: string;
}

export interface FinanceSettingCode {
    service: Entity;
    material: Entity;
}

export interface PrepaymentSetting {
    _id: string;
    name: string;
    defaultUoM: UoM;
    saleTaxRate?: TaxRate;
    purchaseTaxRate?: TaxRate;
}

export interface ConsolidatedInvoicesBills {
    invoiceLineName: string;
    invoiceLineValue: string;
    invoiceTheme: Entity;
    billLineValue: string;
}

export interface RegionalSettings {
    locale?: string;
    timezone?: string;
    dateFormat?: string;
    timeFormat?: string;
    fiscalYearStartMonth?: number;
    fiscalYearStartDay?: number;
}

export interface SchedulingSettings {
    dayStartTime: string;
    dayEndTime: string;
    workDefaultTime: WorkDefaultTime;
    workWeek: DayShortName[];
    startLocationType: SchedulingLocationType;
    endLocationType: SchedulingLocationType;
    startLocationCompany: Entity;
    endLocationCompany: Entity;
    timezones: string[];
    runsheetMode: SchedulingRunsheetMode;
    futureAppointmentViewLimit: number;
    enableContractorReassignmentInScheduler?: boolean;
}

export interface FinanceSettings {
    isZeroIssue: boolean;
    generateBillsOnFinalise: boolean;
    generatedBillStatus: InvoiceStatus;
    generateInvoicesOnFinalise: boolean;
    appendLongDescription: boolean;
    generatedInvoiceStatus: InvoiceStatus;
    excludeFromGenerateInvoiceOnFinalise: ExcludeSettings[];
    invoiceDateOption: InvoiceDateOption;
    roundingWarningThreshold: number;
    codeService: Entity;
    codeMaterial: Entity;
    labour: CodeFinance;
    code: FinanceSettingCode;
    defaultUoM: UoM;
    prepayment: PrepaymentSetting;
    consolidatedInvoicesBills: ConsolidatedInvoicesBills;
}

export interface ExcludeSettings {
    accounts: Entity[];
    jobTypes: Entity[];
}
export interface TimeBillingSettings {
    defaultDuration: number;
    defaultDuratioModeToTime: boolean;
    autoFieldTimesheets: boolean;
    autoCloseFieldTimesheets: boolean;
    fieldTimesheetStart: string;
    threshold: number;
    defaultFieldTimesheetStatus: TimesheetEntryStatus;
    defaultEmployeeTimesheetStatus: TimesheetEntryStatus;
    defaultWorkType: Entity;
    defaultTravelWorkType: Entity;
    defaultOnSiteWorkType: Entity;
    defaultWorkRole: Entity;
    autoStartTimesheetWhenCheckingOut: boolean;
    splitIntoSegments: boolean;
}

export interface SalesSettings {
    salesMode: SalesMode;

}

export interface OperationAutoCloseSettings {
    finalisedMoreThan: {
        enabled: boolean;
        months: number;
    },
    finalisedAndTagged: {
        enabled: boolean;
        tags: Entity[]
    },
    finalisedAndInvoices: {
        enabled: boolean;
    },
    finalisedAndBills: {
        enabled: boolean;
    }
}

export interface OperationGrossProfitMarginThresholdSettings {
    enabled: boolean;
    threshold: number;
}

export interface OperationSettings {
    useSchedulingEnabled: boolean;
    allowBulkDispatch: boolean;
    allowCreateActivity: boolean;
    // keepQuotedSellPrice: boolean;
    generateDaysInAdvance: number;
    forceNowDate: Date;
    taskMode: OperationTaskMode;
    itemsMode: OperationItemsMode;
    documentThemeMode: DocumentThemeMode;
    jobReportMode: JobReportMode;
    preventCompleteInFutureDates: boolean;
    preventFinaliseInFutureDates: boolean;
    finalisationBackdateLimit: number;
    hideSiteContactFromWorkOrderEmail: boolean;
    defaultOwnerToCurrentUser: boolean;
    grossProfitMarginThreshold: OperationGrossProfitMarginThresholdSettings,
    autoClose: OperationAutoCloseSettings;
    disableAdhocAccountsForJobs: boolean;
    disableAdhocSitesForJobs: boolean;
    disableAdhocContactsForJobs: boolean;
    disableMandatoryContactDetailsForJobs: boolean;
    enableCustomJobNumber: boolean;
    jobDescriptionMandatory: boolean;
    autoSetSellBuyPricebooks: boolean;
    /** Read via `isStageAndItemDeletionPrevented` so the server refusal and the web menus answer alike (SP-18). */
    preventStageAndItemDeletion: boolean;
    strictResourceRecommendations: boolean;
    resourceMode: StageResourceMode;
    subcontractorFieldStaffMode: SubcontractorFieldStaffMode;
    jobTypeMandatory: boolean;
    disableManagedSiteContactCopyToJobs: boolean;
    /** Structure a new job takes when neither caller, template nor job type names one. Read via `getDefaultJobStructure`. */
    defaultJobStructure?: JobMode;
}

export interface SmsSettings {
    fromNumber: string;
}

export interface IndividualComplianceSettings {
    fromEmail: string;
    addtionalNotes: string;
}

export interface ActivitySharingSettings {
    appointmentActivities: ActivityShareType[];
    // userActivities: ActivityShareType[];
    customerToField: boolean;
    fieldToCustomer: boolean;

}

export interface EmailSettings {
    sendFrom: string;
    replyTo: string;
}

export interface SystemSettings {
    systemRoles: boolean;
    consolidateCompaniesInClientPortal: boolean;
    reportRecordLimit: number;
    disableReportStreaming?: boolean;
}

export interface ItemsSettings {
    hideLocation: boolean;
    hideNote: boolean;
    sellPriceFallback: 'TRADE_PRICE' | 'RETAIL_PRICE' | 'ZERO_PRICE';
    disableWriteInLocations: boolean;
    displayAdditionalLineFields?: boolean;

    /**
     * Restricts the contractor portal and field app item pickers to items whose catalogue category
     * carries a work category the person is approved for. Never read this on its own — it only takes
     * effect while `operations.strictResourceRecommendations` is also on. Use
     * `isItemPickerRestrictedByWorkCategory` so the dependency is applied consistently.
     */
    restrictPickerByWorkCategory?: boolean;

    /** Off by default: a pending item holds the claim until it is approved or declined (WBS-12137). On, approving the claim approves its pending items with it, as before that gate (WBS-13379). */
    allowClaimWithPendingItems?: boolean;
}

export interface SorSettings {
    /** Gates the whole SOR capability for the tenant. Off = the system behaves exactly as it did before SOR existed. */
    enabled?: boolean;
    /** Set once per tenant because configuring photo requirements on thousands of SOR codes is not viable. */
    evidence?: SorEvidenceSettings;
    /** Resolved onto a SOR line's task when the task is created, so changing it never rewrites existing tasks. */
    taskGate?: TaskGate;
    /** Off by default because a request normally captures before evidence only (EV-43). */
    allowWorkAlreadyCompletedRequests?: boolean;
    /** Off by default: a pending line's task holds the stage until the line is approved. On, a line recording work already done, with its evidence in place, does not (WBS-13379). */
    workAlreadyCompletedPassesStageCompletion?: boolean;
}

/** One wording for the "Work already completed" setting on both settings pages. */
export const SOR_WORK_ALREADY_COMPLETED_HINT = 'Lets technicians mark a requested SOR line as work already done, so they can capture during and after photos while it waits for approval. When off, pending lines take before photos only.';

/** One wording for the stage completion setting on every settings page. */
export const SOR_WORK_ALREADY_COMPLETED_PASSES_STAGE_HINT = 'Lets a stage be completed while its pending SOR lines await approval, when each is marked work already completed and its evidence is in place.';

/** One wording for the pending items claim setting on every settings page. */
export const ALLOW_CLAIM_WITH_PENDING_ITEMS_HINT = 'Lets a claim be submitted while the stage has pending items. Approving the claim approves them with it; rejecting it declines them.';

export interface CrmSettings {
    duplicateSites: 'Warn' | 'Prevent';
}

export interface ServiceSettings {
    confirmationRequiredOverdueThreshold: number;
}

export interface ContractorCapacitySettings {
    missingCapacityBehavior: 'NoCapacity' | 'UnlimitedCapacity';
    countAssignedStatus: boolean;
    countOnHoldStatus: boolean;
    enablePriceBasedRecommendations: boolean;
    priceBasedRecommendationTieBreaker: 'capacity' | 'alphabetical';
}


export enum CaptureSignatureSignee {
    SiteContact = 'Site Contact',
    SiteManager = 'Site Manager',
    Technician = 'Technician',
    FieldSupervisor = 'Field Supervisor',
}

export interface CaptureSignatureSettings {
    enabled: boolean;
    defaultSignee?: CaptureSignatureSignee;
}

export interface GeneralSetting {
    region: RegionalSettings;
    schedule: SchedulingSettings;
    finance: FinanceSettings;
    sales: SalesSettings;
    operations: OperationSettings;
    timeBilling: TimeBillingSettings;
    sms: SmsSettings;
    fieldImage: PhotoSettings;
    captureSignature: CaptureSignatureSettings;
    activitySharing: ActivitySharingSettings;
    individualCompliance: IndividualComplianceSettings;
    system: SystemSettings;
    items: ItemsSettings;
    sor?: SorSettings;
    crm: CrmSettings;
    services: ServiceSettings;
    contractorCapacity?: ContractorCapacitySettings;
    /**
     * Planned Work policy: how far ahead Visits are persisted and when a Visit becomes a Job.
     * Optional on the stored document — a tenant that has never opened the section has nothing written,
     * so every consumer reads it through `getPlannedWorkSettings` rather than the raw path.
     */
    plannedWork?: PlannedWorkSettings;
}

/**
 * The tenant's Planned Work policy, per-field defaulted.
 *
 * The section is optional on the stored document and its values are a `Mixed` blob, so a partially
 * written or hand-edited value is normal rather than exceptional. Reading through here means one bad
 * value cannot discard the good value beside it, and a tenant that has never visited the section still
 * gets a usable policy instead of an undefined horizon.
 */
export const getPlannedWorkSettings = (settings?: Pick<GeneralSetting, 'plannedWork'> | null): PlannedWorkSettings => normalizePlannedWorkSettings(settings?.plannedWork);

/**
 * The tenant's default job structure. Unset reads as Multi-Stage when the tenant has the Multi-Stage Jobs addon,
 * else Basic (WBS-11720), so it is resolved at the caller against the plan and never stored. A stored value is
 * returned as stored even when its addon was switched off later — refusing it at create time is the validator's job.
 */
export const getDefaultJobStructure = (settings: Pick<GeneralSetting, 'operations'> | null | undefined, entitled: readonly JobMode[]): JobMode =>
    settings?.operations?.defaultJobStructure ?? (entitled.includes('Advanced') ? 'Advanced' : 'Basic');

/**
 * The item picker work category restriction depends on strict resource recommendations — the tenancy has to be
 * maintaining work categories against companies and people for the restriction to produce sensible results. The
 * settings page keeps the two values consistent, but a tenant document written outside it (or one predating the
 * rule) must not be able to activate the restriction on its own, so every consumer resolves it through here.
 */
export const isItemPickerRestrictedByWorkCategory = (settings: GeneralSetting) => settings?.items?.restrictPickerByWorkCategory === true && settings?.operations?.strictResourceRecommendations === true;

export const isStrictResourceRecommendations = (settings: GeneralSetting) => settings?.operations?.strictResourceRecommendations === true;

/** Absent reads as off, so a tenant that never saved the setting keeps deleting. */
export const isStageAndItemDeletionPrevented = (settings?: Pick<GeneralSetting, 'operations'> | null): boolean => settings?.operations?.preventStageAndItemDeletion === true;

export const DefaultSchedulingSettings: Pick<SchedulingSettings, 'dayStartTime' | 'dayEndTime' | 'timezones'> = {
    dayStartTime: '09:00',
    dayEndTime: '17:00',
    timezones: ['Australia/Sydney']
}

export const GENERAL_SETTING_CACHE_TAG = 'general-settings';

export enum GeneralSettingAction {
    Patch = 'PatchGeneralSettingsCommand',
}

export interface PatchGeneralSettingsRequest {
    dirtyBinds: readonly string[];
    patch: Record<string, unknown>;
}